import { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useFeatureFlags } from '../contexts/FeatureFlagContext';
import { usePMFeatures } from '../hooks/useFeatureFlag';
import {
  Box,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Divider,
  BottomNavigation,
  BottomNavigationAction,
  Paper,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Home as HomeIcon,
  Logout as LogoutIcon,
  Handyman as EquipmentIcon,
  BarChart as ReportsIcon,
  AccountCircle as AccountIcon,
  BusinessCenter as JobIcon,
  AdminPanelSettings as AdminIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  QrCodeScanner as ScanIcon,
  Inventory as InventoryIcon,
  AddCircle as ReceiveIcon,
  Folder as ProjectIcon,
  Bolt as QuickActionsIcon,
} from '@mui/icons-material';

const drawerWidth = 200;
const collapsedWidth = 64;

export default function Layout({ children }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(true);
  const { isAuthenticated, logout, user } = useAuth();
  const { isFeatureEnabled } = useFeatureFlags();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleDrawerCollapse = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleNavigation = (path) => {
    navigate(path);
    setMobileOpen(false);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Check if user has admin role
  const isAdmin = user?.roles?.includes('admin');

  // Check if PM features are enabled for this user
  const showPMFeatures = usePMFeatures();

  const menuItems = [
    // Only show Dashboard for PM demo users
    ...(showPMFeatures ? [{ text: 'Dashboard', icon: <HomeIcon />, path: '/' }] : []),
    { text: 'Jobs', icon: <JobIcon />, path: '/jobs' },
    { text: 'Receive', icon: <ReceiveIcon />, path: '/receive' },
    { text: 'Quick Actions', icon: <QuickActionsIcon />, path: '/quick-actions' },
    { text: 'Inventory', icon: <EquipmentIcon />, path: '/inventory' },
    // Only show reports if advanced reporting feature is enabled
    ...(isFeatureEnabled('ADVANCED_REPORTING') ? [{ text: 'Reports & Exports', icon: <ReportsIcon />, path: '/reports' }] : []),
    { text: 'Account', icon: <AccountIcon />, path: '/account' },
    // Only show admin link for users with admin role
    ...(isAdmin ? [{ text: 'Users', icon: <AdminIcon />, path: '/admin' }] : []),
  ];

  // Mobile bottom navigation items - most used features for warehouse workers
  const mobileNavItems = [
    // Only show Dashboard for PM demo users on mobile too
    ...(showPMFeatures ? [{ label: 'Dashboard', icon: <HomeIcon />, path: '/' }] : []),
    { label: 'Jobs', icon: <JobIcon />, path: '/jobs' },
    { label: 'Quick Actions', icon: <QuickActionsIcon />, path: '/quick-actions' },
    { label: 'Inventory', icon: <InventoryIcon />, path: '/inventory' },
    { label: 'Account', icon: <AccountIcon />, path: '/account' },
  ];

  // Get current tab value for bottom navigation
  const getBottomNavValue = () => {
    const currentPath = location.pathname;
    const index = mobileNavItems.findIndex(item => item.path === currentPath);
    return index >= 0 ? index : 0;
  };

  const drawer = (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Toolbar sx={{ justifyContent: drawerOpen ? 'space-between' : 'center' }}>
        {drawerOpen && (
          <Typography variant="h6" noWrap component="div" sx={{ fontWeight: 'bold' }}>
            BAVS Inventory
          </Typography>
        )}
        <IconButton
          onClick={handleDrawerCollapse}
          sx={{ 
            display: { xs: 'none', sm: 'block' },
            ml: drawerOpen ? 0 : 'auto',
            mr: drawerOpen ? 0 : 'auto'
          }}
        >
          {drawerOpen ? <ChevronLeftIcon /> : <ChevronRightIcon />}
        </IconButton>
      </Toolbar>
      <Divider />
      <List>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton 
              onClick={() => handleNavigation(item.path)}
              sx={{
                minHeight: 48,
                justifyContent: drawerOpen ? 'initial' : 'center',
                px: 2.5,
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: drawerOpen ? 3 : 'auto',
                  justifyContent: 'center',
                }}
              >
                {item.icon}
              </ListItemIcon>
              {drawerOpen && <ListItemText primary={item.text} />}
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Divider />
      <List>
        <ListItem disablePadding>
          <ListItemButton 
            onClick={handleLogout}
            sx={{
              minHeight: 48,
              justifyContent: drawerOpen ? 'initial' : 'center',
              px: 2.5,
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: drawerOpen ? 3 : 'auto',
                justifyContent: 'center',
              }}
            >
              <LogoutIcon />
            </ListItemIcon>
            {drawerOpen && <ListItemText primary="Logout" />}
          </ListItemButton>
        </ListItem>
      </List>
    </div>
  );

  return (
    <Box sx={{ display: 'flex', height: '100vh' }}>
      <CssBaseline />
      {/* Mobile menu button - positioned fixed for mobile */}
      {isMobile && (
        <IconButton
          color="inherit"
          aria-label="open drawer"
          edge="start"
          onClick={handleDrawerToggle}
          sx={{ 
            position: 'fixed',
            top: 10,
            left: 10,
            zIndex: theme.zIndex.drawer + 1,
            backgroundColor: 'background.paper',
            boxShadow: 2,
            '&:hover': {
              backgroundColor: 'background.paper',
            }
          }}
        >
          <MenuIcon />
        </IconButton>
      )}
      <Box
        component="nav"
        sx={{ width: { sm: drawerOpen ? drawerWidth : collapsedWidth }, flexShrink: { sm: 0 } }}
        aria-label="menu items"
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerOpen ? drawerWidth : collapsedWidth,
              transition: theme => theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.leavingScreen,
              }),
              overflowX: 'hidden',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 1, sm: 2, md: 2 }, // Responsive padding
          pt: { xs: 7, sm: 3, md: 3 }, // Extra top padding for mobile menu button
          pb: { xs: 8, sm: 3 }, // Extra padding bottom for mobile nav
          width: { sm: `calc(100% - ${drawerOpen ? drawerWidth : collapsedWidth}px)` },
          overflow: 'auto',
          transition: theme => theme.transitions.create(['margin', 'width'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <Outlet />
      </Box>
      
      {/* Mobile Bottom Navigation */}
      {isMobile && isAuthenticated && (
        <Paper 
          sx={{ 
            position: 'fixed', 
            bottom: 0, 
            left: 0, 
            right: 0,
            zIndex: theme.zIndex.appBar,
          }} 
          elevation={3}
        >
          <BottomNavigation
            value={getBottomNavValue()}
            onChange={(event, newValue) => {
              navigate(mobileNavItems[newValue].path);
            }}
            showLabels
          >
            {mobileNavItems.map((item, index) => (
              <BottomNavigationAction
                key={item.path}
                label={item.label}
                icon={item.icon}
                sx={{
                  minWidth: 'auto',
                  padding: '6px 0',
                  '& .MuiBottomNavigationAction-label': {
                    fontSize: '0.75rem',
                  },
                }}
              />
            ))}
          </BottomNavigation>
        </Paper>
      )}
    </Box>
  );
}